import React, { useState, useRef, useEffect, useContext } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from "react-native";
import { DataTable, Checkbox, TextInput } from "react-native-paper";
import { useForm, Controller } from "react-hook-form";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DatePickerModal } from "react-native-paper-dates";
import { useDispatch, useSelector } from "react-redux";
import {
  addFactoryOrderDetails,
  getFactoryOrderDetailsByKey,
  updateFactoryOrderDetails,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import moment from "moment";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useAppTheme } from "../../../hooks/useAppTheme";


// Define types for form data
interface FormData {
  Run: string;
  directShipment: boolean;
  soakarPad: boolean;
  labelOrder: boolean;
  PlannedQty: number | null;
  Duration: number | null;
  AllocateProductionLine: number | null;
  PlanStartDay: Date | null;
  ActualStartDay: Date | null;
  ActualCompletionDate: Date | null;
  ESTCompletion: Date | null;
  ETD: Date | null;
  ETA: Date | null;
  ExpectedArrival: Date | null;
  ShippingDeadline: Date | null;
  Remarks: string | null;
}

// Define props for the component
interface ProductionTrackingFormProps {
  isFormMode?: boolean;
  initialData?: any;
  onCancel: () => void;
  editingRowIndex?: number | null;
  currentRow: string;
  isEditMode: boolean;
  formModeActionNumber?: number | null;
  prefixRow: any;
}

// Field definition type
interface FieldDefinition {
  key: keyof FormData;
  name: string;
  type: "checkbox" | "date" | "number" | "string";
  editable?: boolean;
}

const ProductionTrackingForm: React.FC<ProductionTrackingFormProps> = ({
  isFormMode = true,
  initialData,
  onCancel,
  editingRowIndex = null,
  currentRow,
  isEditMode,
  formModeActionNumber,
  prefixRow,
}) => {
  const [showPlanDate, setShowPlanDate] = useState<boolean>(
    formModeActionNumber !== null
  );
  const [showActualDate, setShowActualDate] = useState<boolean>(false);
  const [showCompletionDate, setShowCompletionDate] = useState<boolean>(false);

  const { loadingRunOrdersAction: loadingAddFlag } = useSelector(
    (state: any) => state.chinaTracker
  );
  const { isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const scrollViewRef = useRef<ScrollView>(null);
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);

  const [plannedQtyWarning, setPlannedQtyWarning] = useState<number | null>(
    null
  );

  const handleShippingDeadline = () => {
    if (!prefixRow?.RequestDate) return null;

    // Ensure we have a string and trim safely
    const requestDate = moment(prefixRow.RequestDate.trim(), "DD/MM/YYYY");

    if (!requestDate.isValid()) return null;

    const state = prefixRow.StateDelivery;
    const transitDays = state === "NSW" || state === "VIC" ? 14 : 30;

    // Shipping deadline: request date + transit days + buffer (7 days)
    const shippingDeadline = requestDate
      .clone()
      .subtract(transitDays + 7, "days");
    return shippingDeadline.toDate(); // return as JS Date
  };
  const { control, reset, handleSubmit, setValue, getValues, watch } =
    useForm<FormData>({
      defaultValues: {
        directShipment: false,
        soakarPad: false,
        labelOrder: false,
        AllocateProductionLine: null,
        Duration: null,
        PlanStartDay: null,
        ActualStartDay: null,
        ActualCompletionDate: null,
        ESTCompletion: null,
        ETD: null,
        ETA: null,
        ExpectedArrival: null,
        ShippingDeadline: handleShippingDeadline(),
        Remarks: null,
      },
    });

  // Determine if we should use vertical layout
  const useVerticalLayout = isSmallDevice || isMediumDevice;

  useEffect(() => {
    if (
      initialData &&
      Object.keys(initialData).length > 0 &&
      formModeActionNumber !== null &&
      formModeActionNumber !== undefined
    ) {
      const data = initialData?.[formModeActionNumber ?? 1];

      if (data.actualStartDay) {
        setShowActualDate(true);
      }

      if (data.actualCompletionDate) {
        setShowCompletionDate(true);
      }

      const runOrderNumber =
        formModeActionNumber === null
          ? initialData.length + 1
          : formModeActionNumber + 1;

      // Edit mode: map initialData to FormData shape
      const formattedData: FormData = {
        Run: runOrderNumber.toString(),
        directShipment: data.directShipment || false,
        soakarPad: data.soakarPad || false,
        labelOrder: data.labelOrder || false,
        AllocateProductionLine: data.allocateProductionLine || 0,
        Duration: data.duration || 0,
        PlannedQty: data.plannedQty || 0,
        PlanStartDay: data.planStartDay ? new Date(data.planStartDay) : null,
        ActualCompletionDate: data.ActualCompletionDate
          ? new Date(data.ActualCompletionDate)
          : null,
        ActualStartDay: data.actualStartDay
          ? new Date(data.actualStartDay)
          : null,
        ESTCompletion: data.ESTCompletionDate
          ? new Date(data.ESTCompletionDate)
          : new Date(),
        ETD: data.ETD ? new Date(data.ETD) : new Date(),
        ETA: data.eta ? new Date(data.ETA) : new Date(),
        ExpectedArrival: data.ETA ? new Date(data.ExpectedArrival) : new Date(),
        ShippingDeadline: data.shippingDeadline
          ? new Date(data.shippingDeadline)
          : new Date(),
        Remarks: data.remarks || "",
      };

      reset(formattedData); // populate form fields
    } else {
      // setValue("Duration", "");
    }
  }, [initialData, reset, formModeActionNumber]);

  // Field definitions matching the exact headers from the image
  const formFields: FieldDefinition[] = [
    {
      key: "Run",
      name: "",
      type: "string",
      editable: false,
    },
    {
      key: "directShipment",
      name: "directShipment",
      type: "checkbox",
      editable: false,
    },
    {
      key: "soakarPad",
      name: "soakarPad",
      type: "checkbox",
      editable: true,
    },
    {
      key: "labelOrder",
      name: "labelOrder",
      type: "checkbox",
      editable: true,
    },
    {
      key: "PlannedQty",
      name: "plannedQuantity",
      type: "number",
      editable: true,
    },
    {
      key: "Duration",
      name: "duration",
      type: "number",
      editable: true,
    },
    {
      key: "AllocateProductionLine",
      name: "allocateProductionLine",
      type: "number",
      editable: true,
    },
    {
      key: "PlanStartDay",
      name: "planStartDay",
      type: "date",
      editable: formModeActionNumber === null ? true : false,
    },
    {
      key: "ActualStartDay",
      name: "actualStartDay",
      type: "date",
      editable: formModeActionNumber === null ? false : true,
    },
    {
      key: "ESTCompletion",
      name: "estCompletion",
      type: "date",
      editable: false,
    },
    {
      key: "ActualCompletionDate",
      name: "actualCompletionDate",
      type: "date",
      editable: formModeActionNumber === null ? false : true,
    },
    {
      key: "ShippingDeadline",
      name: "shippingDeadline",
      type: "date",
      editable: false,
    },
    { key: "ETD", name: "etd", type: "date", editable: false },
    { key: "ETA", name: "eta", type: "date", editable: false },
    {
      key: "ExpectedArrival",
      name: "expectedArrival",
      type: "date",
      editable: false,
    },
  ];

  const getFieldLabel = (field: FieldDefinition) => {
    const translatedLabel = translateFields(
      field.key as string,
      t,
      chinaTrackerTranslationMap
    );
    if (translatedLabel && translatedLabel.trim().length > 0) {
      return translatedLabel;
    }
    if (field.key === "Run") {
      return t("run");
    }
    return field.name;
  };

  const handleInputDateChange = (field: FieldDefinition, value: any) => {
    const state = prefixRow.StateDelivery;
    const requestDate = moment(prefixRow.RequestDate.trim(), "DD/MM/YYYY");

    if (field.key === "PlanStartDay" || field.key === "ActualStartDay") {
      const startDate = moment(value);
      const duration = getValues("Duration");

      let estCompletionDate;

      if (!duration) {
        // Estimate completion date
        const day = startDate.isoWeekday(); // 1 (Mon) - 7 (Sun)
        if (day === 1 || day === 2) {
          estCompletionDate = startDate.clone().add(7, "days");
        } else {
          const daysUntilNextFriday = 12 - day;
          estCompletionDate = startDate
            .clone()
            .add(daysUntilNextFriday, "days");
        }
      } else {
        estCompletionDate = startDate.clone().add(duration, "days");
      }

      const etd = estCompletionDate.clone().add(7, "days");

      const transitDays = state === "NSW" || state === "VIC" ? 14 : 30;

      const eta = etd.clone().add(transitDays, "days");

      const shippingDeadline = requestDate
        .clone()
        .subtract(transitDays + 7, "days");

      const expectedArrival = eta.clone().add(7, "days");

      setValue("ESTCompletion", estCompletionDate.toDate());
      setValue("ETD", etd.toDate());
      setValue("ETA", eta.toDate());
      setValue("ExpectedArrival", expectedArrival.toDate());
      setValue("ShippingDeadline", shippingDeadline.toDate());

      // If ActualCompletion exists, apply second case logic
      const actualCompletionRaw = getValues("ActualCompletionDate");
      if (actualCompletionRaw) {
        const actualCompletionDate = moment(actualCompletionRaw);
        if (actualCompletionDate.isValid()) {
          const actualETD = actualCompletionDate.clone().add(7, "days");
          const actualETA = actualETD.clone().add(transitDays, "days");
          const actualExpectedArrival = actualETA.clone().add(7, "days");

          setValue("ETD", actualETD.toDate());
          setValue("ETA", actualETA.toDate());
          setValue("ExpectedArrival", actualExpectedArrival.toDate());
        }
      }
    } else {
      //  ActualCompletion date logic only
      const actualCompletionDate = moment(value);
      if (actualCompletionDate.isValid()) {
        const etd = actualCompletionDate.clone().add(7, "days");
        const transitDays = state === "NSW" || state === "VIC" ? 14 : 30;
        const eta = etd.clone().add(transitDays, "days");
        const expectedArrival = eta.clone().add(7, "days");

        setValue("ETD", etd.toDate());
        setValue("ETA", eta.toDate());
        setValue("ExpectedArrival", expectedArrival.toDate());
      }
    }
  };

  const handleDurationDays = (durationNumber: number) => {
    const planStartDate = getValues("PlanStartDay");
    const actualStartDate = getValues("ActualStartDay");
    const actualCompletionDate = getValues("ActualCompletionDate");

    // Prefer PlanStartDay, then ActualStartDay
    const baseStartDate = planStartDate || actualStartDate;

    if (baseStartDate) {
      handleInputDateChange(
        {
          key: planStartDate ? "PlanStartDay" : "ActualStartDay",
        } as FieldDefinition,
        baseStartDate
      );
    }

    // If ActualCompletionDate exists, trigger logic for that too
    if (actualCompletionDate) {
      handleInputDateChange(
        { key: "ActualCompletionDate" } as FieldDefinition,
        actualCompletionDate
      );
    }
  };

  const toast = useToast();

  const onFormSubmit = async (data: FormData) => {
    // Gather form values
    const {
      labelOrder,
      directShipment,
      soakarPad,
      PlannedQty,
      AllocateProductionLine,
      PlanStartDay,
      ActualStartDay,
      Remarks,
      ActualCompletionDate,
      Duration,
      ESTCompletion,
      ETD,
      ETA,
      ExpectedArrival,
      ShippingDeadline,
    } = data;

    if (!PlannedQty) {
      toast.show(t("pleaseFillPlannedQty"), {
        type: "danger",
      });
      return;
    }

    if (AllocateProductionLine === null) {
      toast.show(t("pleaseSelectProductionLine"), {
        type: "danger",
      });
      return;
    }

    if (!Duration) {
      toast.show(t("pleaseEnterDuration"), {
        type: "danger",
      });
      return;
    }

    if (!PlanStartDay && !ActualCompletionDate && !ActualStartDay) {
      toast.show(t("pleaseSelectDate"), {
        type: "danger",
      });
      return;
    }

    // Prepare the payload for the API request
    const payload = {
      key: currentRow.toString(),
      orderNumber: prefixRow.OrderNumber1,
      runOrder:
        formModeActionNumber === null
          ? initialData.length + 1
          : formModeActionNumber !== null && formModeActionNumber !== undefined
          ? formModeActionNumber + 1
          : 1,
      directShipment: directShipment,
      soakarPad: soakarPad,
      labelOrder: labelOrder,
      plannedQty: PlannedQty.toString().replace(/,/g, ""),
      allocateProductionLine: AllocateProductionLine,
      planStartDay: PlanStartDay ? PlanStartDay : null,
      actualStartDay: ActualStartDay ? ActualStartDay : null,
      actualCompletionDate: ActualCompletionDate ? ActualCompletionDate : null,
      remarks: Remarks || "",
      state: prefixRow.StateDelivery,

      duration: data.Duration,
      ESTCompletionDate: ESTCompletion,
      ETD: ETD,
      ETA: ETA,
      ExpectedArrival: ExpectedArrival,
      shippingDeadline: ShippingDeadline,
    };

    // Determine which action to dispatch
    let action;

    if (
      isEditMode === true &&
      formModeActionNumber !== null &&
      formModeActionNumber !== undefined
    ) {
      // Editing an existing order
      action = dispatch(
        updateFactoryOrderDetails({
          orderId: initialData[formModeActionNumber]._id,
          payload,
        })
      );
    } else {
      // Creating a new order
      action = dispatch(addFactoryOrderDetails(payload));
    }

    // Execute action and handle promise chain
    action
      .unwrap()
      .then(() => {
        toast.show(t("factoryOrderCreateSuccess"), {
          type: "success",
        });

        // Close form
        onCancel();

        // Refresh factory order details
        return dispatch(getFactoryOrderDetailsByKey(currentRow));
      })
      .catch((error: any) => {
        console.error(error, "Error");
        toast.show(`${t("factoryOrderError")} ${error}`, {
          type: "danger",
        });
      });
  };

  const renderField = (field: FieldDefinition, index: number) => {
    if (index === 0) {
      return (
        <Text style={useVerticalLayout ? styles.verticalFieldValue : { color: themeColors.text }}>
          {t("run")}{" "}
          {formModeActionNumber === null
            ? initialData.length + 1
            : formModeActionNumber !== null &&
              formModeActionNumber !== undefined
            ? formModeActionNumber + 1
            : 1}
        </Text>
      );
    }

    if (field.type === "checkbox") {
      return (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { onChange, value } }) => (
            <View
              style={
                useVerticalLayout
                  ? styles.verticalCheckboxContainer
                  : styles.checkboxContainer
              }
            >
              <Checkbox
                status={value ? "checked" : "unchecked"}
                onPress={() => onChange(!value)}
                disabled={!field.editable}
                color="#2196F3"
                uncheckedColor={themeColors.textSecondary}
                theme={{ colors: { onSurfaceVariant: themeColors.textSecondary, disabled: themeColors.textSecondary, onSurface: themeColors.textSecondary } }}
              />
            </View>
          )}
        />
      );
    }

    if (field.type === "date") {
      return (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { onChange, value }, fieldState }) => {
            return (
              <View
                style={[
                  useVerticalLayout
                    ? styles.verticalDateButton
                    : styles.dateButton,
                  !field.editable &&
                    (useVerticalLayout
                      ? styles.verticalDisabledDateButton
                      : styles.disabledDateButton),
                ]}
              >
                {!field.editable ? (
                  <View
                    style={
                      useVerticalLayout
                        ? styles.verticalDateButtonInner
                        : styles.dateButtonInner
                    }
                  >
                    <Text
                      style={[
                        useVerticalLayout
                          ? styles.verticalDateText
                          : styles.dateText,
                        !field.editable && styles.disabledText,
                      ]}
                    >
                      {value ? moment(value).format("DD/MM/YYYY") : ""}
                    </Text>
                    <View
                      style={
                        useVerticalLayout
                          ? styles.verticalCalendarIcon
                          : styles.calendarIcon
                      }
                    >
                      <MaterialCommunityIcons
                        name="calendar-edit"
                        size={20}
                        color="#0000"
                      />
                    </View>
                  </View>
                ) : (
                  <View
                    style={{ paddingHorizontal: useVerticalLayout ? 0 : 10 }}
                  >
                    {field.key === "PlanStartDay" && (
                      <>
                        <>
                          <TouchableOpacity
                            onPress={() =>
                              field.editable && setShowPlanDate(true)
                            }
                            disabled={!field.editable}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexDirection: "row",
                            }}
                          >
                            <Text style={{ color: themeColors.text }}>
                              {value instanceof Date
                                ? moment(value).format("DD/MM/YYYY")
                                : "DD/MM/YYYY"}
                            </Text>
                            <MaterialCommunityIcons name="calendar" size={24} color={themeColors.text} />
                          </TouchableOpacity>
                          <DatePickerModal
                            locale="en-GB"
                            mode="single"
                            visible={showPlanDate}
                            onDismiss={() => setShowPlanDate(false)}
                            date={value instanceof Date ? value : undefined}
                            onConfirm={(params: any) => {
                              setShowPlanDate(false);
                              if (params.date) {
                                onChange(params.date);
                                handleInputDateChange(field, params.date);
                              }
                            }}
                          />
                        </>
                      </>
                    )}

                    {field.key === "ActualStartDay" && (
                      <>
                        <>
                          <TouchableOpacity
                            onPress={() =>
                              field.editable && setShowActualDate(true)
                            }
                            disabled={!field.editable}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexDirection: "row",
                            }}
                          >
                            <Text style={{ color: themeColors.text }}>
                              {value instanceof Date
                                ? moment(value).format("DD/MM/YYYY")
                                : "DD/MM/YYYY"}
                            </Text>
                            <MaterialCommunityIcons name="calendar" size={24} color={themeColors.text} />
                          </TouchableOpacity>
                          <DatePickerModal
                            locale="en-GB"
                            mode="single"
                            visible={showActualDate}
                            onDismiss={() => setShowActualDate(false)}
                            date={value instanceof Date ? value : undefined}
                            onConfirm={(params: any) => {
                              setShowActualDate(false);
                              if (params.date) {
                                onChange(params.date);
                                handleInputDateChange(field, params.date);
                              }
                            }}
                          />
                        </>
                      </>
                    )}

                    {field.key === "ActualCompletionDate" && (
                      <>
                        <>
                          <TouchableOpacity
                            onPress={() =>
                              field.editable && setShowCompletionDate(true)
                            }
                            disabled={!field.editable}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexDirection: "row",
                            }}
                          >
                            <Text style={{ color: themeColors.text }}>
                              {value instanceof Date
                                ? moment(value).format("DD/MM/YYYY")
                                : "DD/MM/YYYY"}
                            </Text>
                            <MaterialCommunityIcons name="calendar" size={24} color={themeColors.text} />
                          </TouchableOpacity>
                          <DatePickerModal
                            locale="en-GB"
                            mode="single"
                            visible={showCompletionDate}
                            onDismiss={() => setShowCompletionDate(false)}
                            date={value instanceof Date ? value : undefined}
                            onConfirm={(params: any) => {
                              setShowCompletionDate(false);
                              if (params.date) {
                                onChange(params.date);
                                handleInputDateChange(field, params.date);
                              }
                            }}
                          />
                        </>
                      </>
                    )}
                  </View>
                )}
              </View>
            );
          }}
        />
      );
    }

    if (field.type === "number") {
      return (
        <Controller
          control={control}
          name={field.key as keyof FormData}
          render={({ field: { onChange, value } }) => {
            const handleNumberChange = (text: string) => {
              const numericText = text.replace(/[^0-9]/g, "");

              if (field.key === "AllocateProductionLine") {
                // Limit production line to 0-14
                // 0 = vendor
                const num = parseInt(numericText);
                if (numericText === "" || (num >= 0 && num <= 14)) {
                  onChange(numericText === "" ? "" : num);
                }
              } else if (field.key === "PlannedQty") {
                /** Disable max restriction but only show on placeholder **/
                // Calculate maximum allowed planned quantity
                // const totalOrdered = prefixRow?.QtyToShip || 0;
                // const existingPlannedQty =
                //   initialData?.reduce(
                //     (sum: number, item: any, index: number) => {
                //       // Skip current item if editing
                //       if (isEditMode && index === formModeActionNumber) {
                //         return sum;
                //       }
                //       return sum + (item?.plannedQty || 0);
                //     },
                //     0
                //   ) || 0;
                // const maxAllowed = totalOrdered - existingPlannedQty;
                // const num = parseInt(numericText);
                // if (numericText === "" || (num >= 0 && num <= maxAllowed)) {
                //   onChange(numericText === "" ? "" : num);
                // }

                const num = parseInt(numericText);

                const totalOrdered = prefixRow?.QtyToShip || 0;
                const existingPlannedQty =
                  initialData?.reduce(
                    (sum: number, item: any, index: number) => {
                      if (isEditMode && index === formModeActionNumber)
                        return sum;
                      return sum + (item?.plannedQty || 0);
                    },
                    0
                  ) || 0;

                const maxAllowed = totalOrdered - existingPlannedQty;

                if (numericText === "" || !isNaN(num)) {
                  onChange(numericText === "" ? "" : num);

                  // Show warning if exceeded
                  if (num > maxAllowed) {
                    setPlannedQtyWarning(maxAllowed);
                  } else {
                    setPlannedQtyWarning(null);
                  }
                }
              } else {
                if (field.key === "Duration") {
                  if (numericText && numericText != "") {
                    const parsed = parseInt(numericText);
                    onChange(parsed);
                    handleDurationDays(parsed);
                  }
                } else {
                  // Default number handling for other fields
                  onChange(numericText === "" ? "" : parseInt(numericText));
                }
              }
            };

            const getPlaceholder = () => {
              if (field.key === "AllocateProductionLine") {
                return "";
              } else if (field.key === "PlannedQty") {
                const totalOrdered = prefixRow?.QtyToShip || 0;
                const existingPlannedQty =
                  initialData?.reduce(
                    (sum: number, item: any, index: number) => {
                      if (isEditMode && index === formModeActionNumber) {
                        return sum;
                      }
                      return sum + (item?.plannedQty || 0);
                    },
                    0
                  ) || 0;
                const maxAllowed = totalOrdered - existingPlannedQty;
                return `${t(
                  "plannedQtyWarning"
                )} (${maxAllowed.toLocaleString()})`;
              } else if (field.key === "Duration") {
                return t("enterDurationDays");
              }
              return "";
            };

            return (
              <>
                <TextInput
                  style={[
                    useVerticalLayout
                      ? styles.verticalNumberInput
                      : styles.numberInput,
                    !useVerticalLayout &&
                      field.key === "PlannedQty" && { width: "100%" },
                    field.key === "Duration" && {
                      width: "100%",
                    },
                  ]}
                  value={value?.toLocaleString() || ""}
                  onChangeText={handleNumberChange}
                  placeholder={getPlaceholder()}
                  disabled={!field.editable}
                  underlineColor="transparent"
                  keyboardType="numeric"
                  textColor={themeColors.text}
                  placeholderTextColor={themeColors.textSecondary}
                  theme={{ colors: { background: "transparent", text: themeColors.text, placeholder: themeColors.textSecondary, onSurfaceVariant: themeColors.textSecondary } }}
                />
              </>
            );
          }}
        />
      );
    }
    return (
      <Controller
        control={control}
        name={field.key as keyof FormData}
        render={({ field: { onChange, value } }) => (
          <TextInput
            style={
              useVerticalLayout ? styles.verticalTextInput : styles.textInput
            }
            value={value?.toString() || ""}
            onChangeText={onChange}
            placeholder=""
            disabled={!field.editable}
            underlineColor="transparent"
            textColor={themeColors.text}
            placeholderTextColor={themeColors.textSecondary}
            theme={{ colors: { background: "transparent", text: themeColors.text, placeholder: themeColors.textSecondary, onSurfaceVariant: themeColors.textSecondary } }}
          />
        )}
      />
    );
  };

  const renderVerticalForm = () => (
    <ScrollView
      style={styles.verticalContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.verticalFormContainer}>
        {formFields.map((field, index) => (
          <View key={field.key} style={styles.verticalFieldContainer}>
            <Text style={styles.verticalFieldLabel}>
              {getFieldLabel(field)}
            </Text>
            <View style={styles.verticalFieldInputContainer}>
              {renderField(field, index)}
            </View>
          </View>
        ))}

        {/* Remarks Section */}
        <View style={styles.verticalFieldContainer}>
          <Text style={styles.verticalFieldLabel}>{t("remarks")}</Text>
          <Controller
            control={control}
            name="Remarks"
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={styles.verticalRemarksInput}
                value={value?.toString() || ""}
                onChangeText={onChange}
                placeholder={t("remarks")}
                multiline
                numberOfLines={4}
                textColor={themeColors.text}
                placeholderTextColor={themeColors.textSecondary}
                theme={{ colors: { background: "transparent", text: themeColors.text, placeholder: themeColors.textSecondary, onSurfaceVariant: themeColors.textSecondary } }}
              />
            )}
          />
        </View>

        {/* Action Buttons */}
        {plannedQtyWarning !== null && (
          <View style={styles.verticalFieldContainer}>
            <Text style={{ color: "orange", fontSize: 12, marginTop: 4 }}>
              {`${t(
                "plannedQtyWarning"
              )} (${plannedQtyWarning.toLocaleString()})`}
            </Text>
          </View>
        )}
        <View style={styles.verticalButtonContainer}>
          <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
            <Text style={styles.submitButtonText}>{t("cancel")}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.submitButton}
            onPress={handleSubmit(onFormSubmit)}
          >
            <Text style={styles.submitButtonText}>{t("submit")}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );

  const renderHorizontalForm = () => (
    <ScrollView
      horizontal={true}
      style={{ overflowX: "scroll" } as any}
      contentContainerStyle={{ flexDirection: "row" }}
      showsHorizontalScrollIndicator
      ref={scrollViewRef}
    >
      <DataTable style={styles.dataTable}>
        {/* Header Row */}
        <DataTable.Header style={styles.tableHeader}>
          {formFields.map((field) => (
            <DataTable.Title
              key={`header_${field.key}`}
              style={[
                styles.headerCell,
                field.type === "checkbox"
                  ? styles.checkboxCell
                  : styles.dateCell,
              ]}
            >
              <Text style={styles.headerText}>
                {translateFields(field.key, t, chinaTrackerTranslationMap)}
              </Text>
            </DataTable.Title>
          ))}
          <DataTable.Title style={styles.actionCell}>
            <Text style={styles.headerText}></Text>
          </DataTable.Title>
        </DataTable.Header>

        {/* Data Row */}
        <DataTable.Row style={styles.dataRow}>
          {formFields.map((field, index) => (
            <DataTable.Cell
              key={`cell_${field.key}_${index}`}
              style={[
                styles.dataCell,
                field.type === "checkbox"
                  ? styles.checkboxCell
                  : styles.dateCell2,
              ]}
            >
              {renderField(field, index)}
            </DataTable.Cell>
          ))}
          <DataTable.Cell style={styles.actionCell}>
            <TouchableOpacity
              style={{ display: "none" }}
              onPress={handleSubmit(onFormSubmit)}
            >
              <Text style={styles.submitButtonText}>{t("submit")}</Text>
            </TouchableOpacity>
          </DataTable.Cell>
        </DataTable.Row>

        {/* Remarks */}
        <DataTable.Row style={styles.dataRow}>
          <DataTable.Cell style={[styles.dataCell]}>
            <Controller
              control={control}
              name="Remarks"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  style={styles.remarksInput}
                  value={value?.toString() || ""}
                  onChangeText={onChange}
                  placeholder={t("remarks")}
                  textColor={themeColors.text}
                  placeholderTextColor={themeColors.textSecondary}
                  theme={{ colors: { background: "transparent", text: themeColors.text, placeholder: themeColors.textSecondary, onSurfaceVariant: themeColors.textSecondary } }}
                />
              )}
            />
          </DataTable.Cell>
        </DataTable.Row>

        {plannedQtyWarning !== null && (
          <DataTable.Row style={styles.dataRow}>
            <DataTable.Cell style={[styles.dataCell]}>
              <Text style={{ color: "orange", fontSize: 12, marginTop: 4 }}>
                {`${t(
                  "plannedQtyWarning"
                )} (${plannedQtyWarning.toLocaleString()})`}
              </Text>
            </DataTable.Cell>
          </DataTable.Row>
        )}

        {/* Buttons */}
        <DataTable.Row>
          <DataTable.Cell
            style={[styles.actionCell, { marginBottom: "1%", marginTop: "1%" }]}
          >
            <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
              <Text style={styles.submitButtonText}>{t("cancel")}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.submitButton}
              onPress={handleSubmit(onFormSubmit)}
            >
              <Text style={styles.submitButtonText}>{t("submit")}</Text>
            </TouchableOpacity>
          </DataTable.Cell>
        </DataTable.Row>
      </DataTable>
    </ScrollView>
  );

  if (!isFormMode) return null;

  return (
    <View style={styles.container}>
      {loadingAddFlag ? (
        <View
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <Spinner size="small" color="#56B7FD" />
          <Text style={{ marginLeft: 8, color: "#56B7FD", fontSize: 14 }}>
            {t("loading")}
          </Text>
        </View>
      ) : useVerticalLayout ? (
        renderVerticalForm()
      ) : (
        renderHorizontalForm()
      )}
    </View>
  );
};

function createStyles(colors: any) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  dataTable: {
    minWidth: Dimensions.get("window").width,
    padding: 20,
  },
  tableHeader: {
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    paddingHorizontal: 0,
    paddingVertical: 0,
    height: 40,
  },
  headerCell: {
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 0,
  },
  headerText: {
    fontWeight: "700",
    fontSize: 13,
    textAlign: "center",
    color: colors.text,
  },
  dataRow: {
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomColor: "#eee",
    minHeight: 50,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  dataCell: {
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 0,
    paddingVertical: 4,
  },
  checkboxCell: {
    width: 110,
  },
  dateCell: {
    width: 150,
  },
  dateCell2: {
    width: 250,
  },
  actionCell: {
    width: 100,
    justifyContent: "center",
    alignItems: "center",
  },
  buttonActionCell: {
    justifyContent: "space-between",
    alignItems: "center",
  },
  checkboxContainer: {
    width: "100%",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  textInput: {
    width: "100%",
    height: 40,
    backgroundColor: "transparent",
    fontSize: 14,
  },
  remarksInput: {
    borderColor: colors.border,
    borderWidth: 1,
    width: 620,
    height: 30,
    borderRadius: 5,
    backgroundColor: "transparent",
  },
  numberInput: {
    width: 42,
    height: 30,
    borderRadius: 5,
    borderColor: colors.border,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  dateButton: {
    width: "100%",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  disabledDateButton: {
    backgroundColor: colors.surfaceSubtle,
    color: colors.textSecondary,
    borderRadius: 5,
    height: 30,
    width: 90,
    textAlign: "center",
  },
  dateButtonInner: {
    flexDirection: "row",
    alignItems: "center",
    width: "90%",
    height: 36,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 0,
    paddingHorizontal: 8,
  },
  dateText: {
    flex: 1,
    color: colors.text,
    fontSize: 14,
  },
  disabledText: {
    color: colors.textSecondary,
  },
  calendarIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  submitButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginHorizontal: 5,
    borderRadius: 5,
  },
  cancelButton: {
    backgroundColor: "#B3B2B2",
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginHorizontal: 5,
    borderRadius: 5,
  },
  submitButtonText: {
    color: colors.surface,
    fontWeight: "500",
    fontSize: 14,
  },
  dateInputContainer: {
    borderRadius: 5,
    backgroundColor: "white",
  },
  disabledInput: {
    opacity: 0.4,
  },
  icon: {
    alignSelf: "center",
    marginRight: 8,
    color: colors.text,
  },
  // Vertical Layout Styles
  verticalContainer: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  verticalFormContainer: {
    padding: 20,
  },
  verticalFieldContainer: {
    marginBottom: 20,
  },
  verticalFieldLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.text,
    marginBottom: 8,
  },
  verticalFieldInputContainer: {
    width: "100%",
  },
  verticalFieldValue: {
    fontSize: 16,
    color: colors.text,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: colors.surfaceSubtle,
    borderRadius: 8,
  },
  verticalCheckboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
  },
  verticalTextInput: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    minHeight: 48,
  },
  verticalNumberInput: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    minHeight: 48,
    width: "100%",
  },
  verticalDateButton: {
    width: "100%",
    minHeight: 48,
    justifyContent: "center",
  },
  verticalDisabledDateButton: {
    backgroundColor: colors.surfaceSubtle,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  verticalDateButtonInner: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    minHeight: 48,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 16,
    backgroundColor: colors.surface,
  },
  verticalDateText: {
    flex: 1,
    color: colors.text,
    fontSize: 16,
  },
  verticalCalendarIcon: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  verticalDateInputContainer: {
    borderRadius: 8,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  verticalRemarksInput: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    minHeight: 100,
    textAlignVertical: "top",
  },
  verticalButtonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
    gap: 12,
  },
});
}

export default ProductionTrackingForm;
